import React, { useEffect, useMemo, useRef } from "react"
import { useGoogleMaps, googleMapsAuthFailed, GoogleMapsApi } from "../../hooks/useGoogleMaps"

interface Props {
	googleMapURL: string
	loadingElement: React.ReactElement
	containerElement: React.ReactElement
	mapElement: React.ReactElement
	defaultCenter: Location
	center?: Location
	zoom: number
    markers: (Location & { color?: "red" | "green" | "blue" | "yellow" })[]
}

type Location = {
    latitude: number;
	longitude: number;
}

const markerIcons = {
	green: "https://maps.google.com/mapfiles/ms/icons/green-dot.png",
	blue: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
	red: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
	yellow: "https://maps.google.com/mapfiles/ms/icons/yellow-dot.png",
}

// Talks to the Google Maps JS API directly; react-google-maps used the deprecated React.createFactory
const Map: React.FC<Props> = ({ googleMapURL, loadingElement, containerElement, mapElement, defaultCenter, center, zoom, markers }) => {

	const maps = useGoogleMaps(googleMapURL)
	// A callback ref, so a replaced map element is noticed rather than left behind a stale ref
	const [ mapDiv, setMapDiv ] = React.useState<HTMLElement | null>(null)
	const mapBuiltOn = useRef<HTMLElement>()
	const [ map, setMap ] = React.useState<GoogleMapsApi>()

	const lat = center?.latitude ?? defaultCenter.latitude
	const lng = center?.longitude ?? defaultCenter.longitude

	useEffect(() => {
		// Google observes the element straight away, so it has to be a real element in the document
		// and after a key rejection Google's own constructor throws, so leave the map empty then
		if (!maps || googleMapsAuthFailed() || !(mapDiv instanceof HTMLElement) || !mapDiv.isConnected || mapBuiltOn.current === mapDiv) {
			return
		}

		mapBuiltOn.current = mapDiv
		setMap(new maps.Map(mapDiv, {
			center: { lat, lng },
			zoom,
			fullscreenControl: false,
			mapTypeControl: false,
			streetViewControl: false,
			zoomControl: false,
		}))
	}, [ maps, mapDiv, lat, lng, zoom ])

	useEffect(() => { map?.setCenter({ lat, lng }) }, [ map, lat, lng ])
	useEffect(() => { map?.setZoom(zoom) }, [ map, zoom ])

	// Callers build a new array on every render, so redraw only when the markers themselves change
	const markersKey = JSON.stringify(markers.filter(m => !!m))
	const visibleMarkers = useMemo(() => JSON.parse(markersKey) as Props["markers"], [ markersKey ])

	useEffect(() => {
		if (!map || !maps) {
			return
		}

		const drawn = visibleMarkers.map(m => new maps.Marker({
			map,
			position: { lat: m.latitude, lng: m.longitude },
			icon: m.color ? markerIcons[m.color] : undefined,
		}))

		return () => drawn.forEach(m => m.setMap(null))
	}, [ map, maps, visibleMarkers ])

	if (!maps) {
		return loadingElement
	}

	return React.cloneElement(containerElement, {}, React.cloneElement(mapElement, { ref: setMapDiv }))
};

export default Map;
